import {
    PARTNER_RULE_UTM_KEYS,
    PARTNER_RULE_UTM_MAX_LEN,
    type PartnerLandingAttribution,
    type PartnerLandingBranding,
    type PartnerLandingBrandingConfig,
    type PartnerLandingBrandingRule,
    type PartnerLandingLogo,
    type PartnerLandingRuleUtmKey,
} from '@/lib/partnerLandingBranding.types';

/** Tagline when `utm_source` is not Kuder; no server config is fetched for that case. */
export const PARTNER_LANDING_INELIGIBLE_TAGLINE = 'Log in and start building your path.' as const;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPartnerLandingLogo(value: unknown): value is PartnerLandingLogo {
    if (!isRecord(value)) {
        return false;
    }
    return typeof value.src === 'string'
        && value.src.length > 0
        && typeof value.alt === 'string'
        && typeof value.key === 'string';
}

const PARTNER_RULE_UTM_KEY_SET = new Set<string>(PARTNER_RULE_UTM_KEYS);

function isPartnerLandingBrandingRuleUtm(value: unknown): boolean {
    if (!isRecord(value)) {
        return false;
    }
    for (const [k, v] of Object.entries(value)) {
        if (!PARTNER_RULE_UTM_KEY_SET.has(k)) {
            return false;
        }
        if (typeof v !== 'string') {
            return false;
        }
        const t = v.trim();
        if (t.length === 0) {
            return false;
        }
        const key = k as PartnerLandingRuleUtmKey;
        if (t.length > PARTNER_RULE_UTM_MAX_LEN[key]) {
            return false;
        }
    }
    const source = value.utm_source;
    return typeof source === 'string' && source.trim().length > 0;
}

function isPartnerLandingBrandingRule(value: unknown): value is PartnerLandingBrandingRule {
    if (!isRecord(value)) {
        return false;
    }
    if (typeof value.taglineSuffix !== 'string' || value.taglineSuffix.length === 0) {
        return false;
    }
    if (value.partner !== undefined && typeof value.partner !== 'string') {
        return false;
    }
    if (value.feature !== undefined && typeof value.feature !== 'string') {
        return false;
    }
    if (value.publicLinkCode !== undefined) {
        if (typeof value.publicLinkCode !== 'string' || value.publicLinkCode.length === 0 || value.publicLinkCode.length > 32) {
            return false;
        }
        /** Match short codes in bundled JSON (e.g. `kuder-support`) and marketing links. */
        if (!/^[a-z0-9_-]+$/i.test(value.publicLinkCode)) {
            return false;
        }
    }
    if (value.externalSourceId !== undefined) {
        if (typeof value.externalSourceId !== 'string' || value.externalSourceId.trim().length === 0) {
            return false;
        }
        if (value.externalSourceId.length > 128) {
            return false;
        }
    }
    if (!isPartnerLandingBrandingRuleUtm(value.utm)) {
        return false;
    }
    if (value.partnerLogo !== undefined && !isPartnerLandingLogo(value.partnerLogo)) {
        return false;
    }
    return true;
}

/** Validates JSON from `GET /api/config/public/partner-landing-branding`. */
export function parsePartnerLandingBrandingConfig(json: unknown): PartnerLandingBrandingConfig | null {
    if (!isRecord(json)) {
        return null;
    }
    if (json.schemaVersion !== 1) {
        return null;
    }
    if (typeof json.defaultTaglineSuffix !== 'string' || json.defaultTaglineSuffix.length === 0) {
        return null;
    }
    if (!isPartnerLandingLogo(json.kuderLogo)) {
        return null;
    }
    if (!Array.isArray(json.rules) || json.rules.length === 0) {
        return null;
    }
    const rules: PartnerLandingBrandingRule[] = [];
    for (const entry of json.rules) {
        if (!isPartnerLandingBrandingRule(entry)) {
            return null;
        }
        rules.push(entry);
    }
    return {
        schemaVersion: 1,
        defaultTaglineSuffix: json.defaultTaglineSuffix,
        kuderLogo: json.kuderLogo,
        rules,
    };
}

export function resolvePartnerLandingBranding(
    attribution: PartnerLandingAttribution,
    config: PartnerLandingBrandingConfig | null,
): PartnerLandingBranding {
    const { isKuderSource, partner, feature, externalSourceId } = attribution;

    if (!isKuderSource) {
        return {
            taglineSuffix: PARTNER_LANDING_INELIGIBLE_TAGLINE,
            logos: [],
        };
    }

    if (config === null) {
        throw new Error('Partner landing branding config is required when utm_source is kuder');
    }

    const matchedRule = config.rules.find(rule =>
        (rule.partner ? rule.partner === partner : true)
        && (rule.feature ? rule.feature === feature : true)
        && (rule.externalSourceId ? rule.externalSourceId === externalSourceId : true),
    );

    const logos: PartnerLandingLogo[] = [];
    if (matchedRule?.partnerLogo) {
        logos.push(matchedRule.partnerLogo);
    }
    logos.push(config.kuderLogo);

    return {
        taglineSuffix: matchedRule?.taglineSuffix ?? config.defaultTaglineSuffix,
        logos,
    };
}
